import { useMemo } from 'react'
import { toast } from 'react-toastify'
import { Combobox } from 'components/UI/combobox/combobox'
import dialogConfirm from 'components/dialogConfirm'
import { notifySuccess } from 'services/toast'
import { useBulkAssignDocumentBrand } from 'services/documentsApi'
import { useBrandOptions } from 'hooks/useBrandOptions'

const NO_BRAND_VALUE = '__NO_BRAND__'

/** Props for the DocumentLinkBrandCombobox bulk action component. */
export interface DocumentLinkBrandComboboxProps {
  selectedDocumentIds: number[]
  onSuccess?: () => void
  buttonLess?: boolean
}

/**
 * Combobox for bulk assigning a brand to selected documents.
 * Supports assigning a brand or unlinking (No Brand).
 */
export function DocumentLinkBrandCombobox(
  props: DocumentLinkBrandComboboxProps
) {
  const bulkAssignMutation = useBulkAssignDocumentBrand()
  const { brandOptions, isLoading } = useBrandOptions()

  const optionsWithUnlink = useMemo(
    () => [
      { label: 'No Brand', value: NO_BRAND_VALUE as string | number },
      ...brandOptions,
    ],
    [brandOptions]
  )

  async function handleSubmit(brandIdOrAction: number | string) {
    const isUnlink = brandIdOrAction === NO_BRAND_VALUE
    const brandId = isUnlink ? null : Number(brandIdOrAction)

    const selectedBrand = isUnlink
      ? null
      : brandOptions.find((b) => b.value === brandId)
    const brandName = selectedBrand?.label || 'selected brand'

    const count = props.selectedDocumentIds.length
    const message = isUnlink
      ? `Remove brand from ${count} document${count !== 1 ? 's' : ''}?`
      : `Assign "${brandName}" to ${count} document${count !== 1 ? 's' : ''}?`

    const confirmed = await dialogConfirm(
      message,
      isUnlink ? 'Remove Brand' : 'Assign Brand'
    )

    if (!confirmed) return

    try {
      const response = await bulkAssignMutation.mutateAsync({
        brand_id: brandId,
        document_ids: props.selectedDocumentIds,
      })

      const updatedCount = response?.updated_count ?? count
      const successMsg = isUnlink
        ? `Successfully removed brand from ${updatedCount} document${updatedCount !== 1 ? 's' : ''}`
        : `Successfully assigned brand to ${updatedCount} document${updatedCount !== 1 ? 's' : ''}`

      notifySuccess(successMsg)
      props.onSuccess?.()
    } catch (error) {
      console.error('Error in bulk assign document brand:', error)
      toast.error('Failed to assign brand. Please try again.')
    }
  }

  return (
    <Combobox
      options={optionsWithUnlink}
      searchPlaceholder="Search brands..."
      placeholder="Assign Brand"
      onSelectAltAction={handleSubmit}
      loading={isLoading}
      buttonLess={props.buttonLess}
      notFoundPlaceholder="No brands found"
    />
  )
}
